import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, Camera, Check, ExternalLink, File, Image, Link, LogOut, Pencil, Search, Shield, ShieldOff, Star, Trash2, UserPlus, Video, X } from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { useTranslation } from "react-i18next";
import { api } from "../../services/api";
import { BUCKET_PACOCA_URL, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { getImageUser, getImageUserNotFound } from "../../global-components/src/utils/auth";
import { Chat as ChatType, FavoriteMessage } from "../../types";
import ImageCropModal from "../../components/image-crop-modal";
import axios from "axios";
import "./group-info-panel.css";
import CreateGroupModal from "../../components/Modals/CreateGroupModal";
import { debounce } from "lodash";
import { MediaMsg, useChatMediaSearch } from "./useChatMediaSearch";
import ExpandableText from "./ExpandableText";

export interface Member {
    id: number;
    name: string;
    user_name: string;
    img_account: string;
    is_admin: boolean;
    is_creator: boolean;
}

interface Props {
    show: boolean;
    onClose: () => void;
    chatSelected: ChatType | null;
    onGroupUpdated: (updated: Partial<ChatType>) => void;
    onLeft: () => void;
    onScrollToMessage?: (id: number) => void;
    initialTab?: "media" | "search" | "favorites" | null;
}

const GroupInfoPanel = ({ show, onClose, chatSelected, onGroupUpdated, onLeft, onScrollToMessage, initialTab }: Props) => {
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const { t } = useTranslation();

    const [members, setMembers] = useState<Member[]>([]);
    const [totalMembers, setTotalMembers] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isCreator, setIsCreator] = useState(false);
    const [onlyAdminPost, setOnlyAdminPost] = useState(false);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [togglingAdminId, setTogglingAdminId] = useState<number | null>(null);
    const [togglingOnlyAdmin, setTogglingOnlyAdmin] = useState(false);

    // "Ver todos" expanded state
    const [showAllMembers, setShowAllMembers] = useState(false);
    const [allMembers, setAllMembers] = useState<Member[]>([]);
    const [memberSearch, setMemberSearch] = useState("");
    const [memberSearchDebounced, setMemberSearchDebounced] = useState("");
    const [loadingAllMembers, setLoadingAllMembers] = useState(false);
    const PREVIEW_LIMIT = 5;
    const ALL_LIMIT = 100;

    // Name editing
    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState("");
    const [savingName, setSavingName] = useState(false);

    // Description
    const [description, setDescription] = useState("");
    const [editingDesc, setEditingDesc] = useState(false);
    const [descValue, setDescValue] = useState("");
    const [savingDesc, setSavingDesc] = useState(false);

    // Avatar
    const [imgSrc, setImgSrc] = useState("");
    const [isCropOpen, setIsCropOpen] = useState(false);
    const [savingImg, setSavingImg] = useState(false);

    // Add member modal
    const [showAddMember, setShowAddMember] = useState(false);

    // Mount/animation control so the panel can slide out before unmounting
    const [rendered, setRendered] = useState(show);
    const [entering, setEntering] = useState(false);

    const chatId = chatSelected?.chat_id;

    useEffect(() => {
        if (show) {
            setRendered(true);
            const raf = requestAnimationFrame(() => setEntering(true));
            return () => cancelAnimationFrame(raf);
        } else {
            setEntering(false);
            const t = setTimeout(() => setRendered(false), 220);
            return () => clearTimeout(t);
        }
    }, [show]);

    useEffect(() => {
        if (!chatId || !chatSelected?.is_group || !show) return;
        setNameValue(chatSelected.group_name ?? "");
        loadMembers();
    }, [chatId, show]);

    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, onClose]);

    const loadMembers = async () => {
        if (!chatId) return;
        setLoadingMembers(true);
        try {
            const res = await api(token).get(`${PACOCA_API_URL}/chats/group/${chatId}/members?limit=${PREVIEW_LIMIT}`);
            setMembers(res.data.members ?? []);
            setTotalMembers(res.data.total ?? 0);
            setIsAdmin(!!res.data.is_admin);
            setIsCreator(!!res.data.is_creator);
            setOnlyAdminPost(!!res.data.only_admin_post);
            setDescription(res.data.description ?? "");
        } catch {
            // silent
        } finally {
            setLoadingMembers(false);
        }
    };

    const loadAllMembers = async (search: string) => {
        if (!chatId) return;
        setLoadingAllMembers(true);
        try {
            const res = await api(token).get(`${PACOCA_API_URL}/chats/group/${chatId}/members?limit=${ALL_LIMIT}&search=${encodeURIComponent(search)}`);
            setAllMembers(res.data.members ?? []);
        } catch {
            // silent
        } finally {
            setLoadingAllMembers(false);
        }
    };

    const debouncedSetSearch = useCallback(debounce((v: string) => setMemberSearchDebounced(v), 400), []);

    useEffect(() => {
        if (showAllMembers) {
            loadAllMembers(memberSearchDebounced);
        }
    }, [memberSearchDebounced, showAllMembers]);

    const openAllMembers = () => {
        setMemberSearch("");
        setMemberSearchDebounced("");
        setAllMembers([]);
        setShowAllMembers(true);
        loadAllMembers("");
    };

    const {
        showMedia, setShowMedia, mediaTab, mediaItems, mediaCursor, mediaHasMore, loadingMedia,
        loadMedia, openMedia, switchMediaTab,
        showMsgSearch, setShowMsgSearch, msgSearchQuery, setMsgSearchQuery, msgResults, loadingMsgSearch,
        debouncedSetMsgSearch, openMsgSearch,
        parsePaths, parseTypes, extractUrl, formatDate, highlightText,
    } = useChatMediaSearch(chatId);

    const [showPermissions, setShowPermissions] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [favorites, setFavorites] = useState<FavoriteMessage[]>([]);
    const [loadingFavorites, setLoadingFavorites] = useState(false);
    const [favoritesCursor, setFavoritesCursor] = useState<number | null>(null);
    const [favoritesHasMore, setFavoritesHasMore] = useState(false);

    const { token: favToken } = useAuth();
    const loadFavorites = async (cursor?: number | null) => {
        if (loadingFavorites || !chatId) return;
        setLoadingFavorites(true);
        try {
            const params = new URLSearchParams({ limit: "20" });
            if (cursor) params.set("cursor", String(cursor));
            const res = await api(favToken).get(`${PACOCA_API_URL}/chats/messages/favorites?${params}`);
            const data: FavoriteMessage[] = res.data.favorites ?? [];
            const filtered = data.filter(f => f.chat_id === chatId);
            setFavorites(prev => cursor ? [...prev, ...filtered] : filtered);
            setFavoritesHasMore(res.data.has_more ?? false);
            setFavoritesCursor(res.data.next_cursor ?? null);
        } catch {
            // silent
        } finally {
            setLoadingFavorites(false);
        }
    };

    // Open at the requested tab when show becomes true
    useEffect(() => {
        if (!show || !initialTab) return;
        if (initialTab === "media") {
            openMedia();
        } else if (initialTab === "search") {
            openMsgSearch();
        } else if (initialTab === "favorites") {
            setShowFavorites(true);
            loadFavorites(null);
        } 
    }, [show, initialTab]);

    // Header title/back button follow whichever sub-panel is open
    const subTitle: string | null =
        showAllMembers ? `${t("chat.group.participants")}${totalMembers > 0 ? ` (${totalMembers})` : ""}`
        : showMedia ? t("chat.group.mediaLinksTitle")
        : showPermissions ? t("chat.group.permissions")
        : showMsgSearch ? t("chat.group.searchMessages")
        : showFavorites ? t("chat.favorites")
        : null;
    const closeSubPanel = () => {
        if (showAllMembers) setShowAllMembers(false);
        else if (showMedia) setShowMedia(false);
        else if (showPermissions) setShowPermissions(false);
        else if (showMsgSearch) setShowMsgSearch(false);
        else if (showFavorites) setShowFavorites(false);
    };


    const saveName = async () => {
        if (!nameValue.trim() || !chatId) return;
        setSavingName(true);
        try {
            const res = await api(token).patch(`${PACOCA_API_URL}/chats/group/${chatId}`, {
                group_name: nameValue.trim(),
            });
            onGroupUpdated({ group_name: res.data.group_name, name: res.data.group_name });
            setMessage(t("chat.group.nameUpdated"), "success");
        } catch {
            setMessage(t("chat.group.errorSaveName"), "error");
        } finally {
            setSavingName(false);
            setEditingName(false);
        }
    };

    const saveDescription = async () => {
        if (!chatId || savingDesc) return;
        setSavingDesc(true);
        try {
            const res = await api(token).patch(`${PACOCA_API_URL}/chats/group/${chatId}`, {
                description: descValue.trim(),
            });
            setDescription(res.data.description ?? "");
            onGroupUpdated({ description: res.data.description ?? null });
            setMessage(t("chat.group.descriptionUpdated"), "success");
            setEditingDesc(false);
        } catch {
            setMessage(t("chat.group.errorSaveDescription"), "error");
        } finally {
            setSavingDesc(false);
        }
    };

    const onAvatarFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => { setImgSrc(reader.result as string); setIsCropOpen(true); };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const uploadAvatar = async (file: File) => {
        if (!chatId) return;
        setSavingImg(true);
        setIsCropOpen(false);
        try {
            const { data: uploadUrls } = await axios.post(
                PACOCA_API_URL + "/generate-upload-urls",
                { files: [{ type: "image" }] },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await axios.put(uploadUrls[0].url, file);

            const res = await api(token).patch(`${PACOCA_API_URL}/chats/group/${chatId}`, {
                group_img: uploadUrls[0].path,
            });
            onGroupUpdated({ group_img: res.data.group_img, img_account: res.data.group_img });
            setMessage(t("chat.group.imageUpdated"), "success");
        } catch {
            setMessage(t("chat.group.errorSaveImage"), "error");
        } finally {
            setSavingImg(false);
        }
    };

    const toggleOnlyAdminPost = async () => {
        if (!chatId || togglingOnlyAdmin) return;
        setTogglingOnlyAdmin(true);
        const newVal = !onlyAdminPost;
        try {
            await api(token).patch(`${PACOCA_API_URL}/chats/group/${chatId}`, {
                only_admin_post: newVal,
            });
            setOnlyAdminPost(newVal);
            onGroupUpdated({ only_admin_post: newVal });
            setMessage(newVal ? t("chat.group.onlyAdminsCanPostActive") : t("chat.group.onlyAdminsCanPostInactive"), "success");
        } catch {
            setMessage(t("chat.group.errorTogglePermission"), "error");
        } finally {
            setTogglingOnlyAdmin(false);
        }
    };

    const toggleAdmin = async (memberId: number, currentIsAdmin: boolean) => {
        if (!chatId) return;
        setTogglingAdminId(memberId);
        try {
            const res = await api(token).post(`${PACOCA_API_URL}/chats/group/${chatId}/members/${memberId}/toggle-admin`);
            setMembers(prev => prev.map(m =>
                m.id === memberId ? { ...m, is_admin: res.data.is_admin } : m
            ));
            setMessage(res.data.is_admin ? t("chat.group.memberPromoted") : t("chat.group.memberDemoted"), "success");
        } catch {
            setMessage(t("chat.group.errorToggleAdmin"), "error");
        } finally {
            setTogglingAdminId(null);
        }
    };

    const removeMember = async (memberId: number) => {
        if (!chatId) return;
        try {
            await api(token).delete(`${PACOCA_API_URL}/chats/group/${chatId}/members/${memberId}`);
            setMembers(prev => prev.filter(m => m.id !== memberId));
            setMessage(t("chat.group.memberRemoved"), "success");
        } catch (err: any) {
            const msg = err?.response?.data?.message || t("chat.group.errorRemoveMember");
            setMessage(msg, "error");
        }
    };

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingGroup, setDeletingGroup] = useState(false);

    const leaveGroup = async () => {
        if (!chatId) return;
        try {
            await api(token).post(`${PACOCA_API_URL}/chats/group/${chatId}/leave`);
            onClose();
            onLeft();
        } catch {
            setMessage(t("chat.group.errorLeaveGroup"), "error");
        }
    };

    const deleteGroup = async () => {
        if (!chatId) return;
        setDeletingGroup(true);
        try {
            await api(token).delete(`${PACOCA_API_URL}/chats/group/${chatId}`);
            onClose();
            onLeft();
        } catch (err: any) {
            const msg = err?.response?.data?.message || t("chat.group.errorDeleteGroup");
            setMessage(msg, "error");
        } finally {
            setDeletingGroup(false);
            setConfirmDelete(false);
        }
    };

    if (!chatSelected?.is_group || !rendered) return null;

    const isCommunityChat = !!chatSelected.is_community_chat;
    // Community chats mirror name, image, description and admins from the community
    const canEditGroupInfo = isAdmin && !isCommunityChat;

    const displayImg = chatSelected.group_img;
    const displayName = chatSelected.group_name ?? t("chat.group.panelTitle");

    return (
        <>
            <ImageCropModal
                id="group-avatar"
                type="profile"
                imageSrc={imgSrc}
                loading={savingImg}
                isOpen={isCropOpen}
                onClose={() => setIsCropOpen(false)}
                onSave={uploadAvatar}
            />

            {showAddMember && (
                <CreateGroupModal
                    show={showAddMember}
                    onClose={() => setShowAddMember(false)}
                    onCreated={() => {}}
                    addMembersToGroup={chatId}
                    onMembersAdded={(newMembers) => {
                        setShowAddMember(false);
                        setMembers(prev => {
                            const existing = new Set(prev.map(m => m.id));
                            const toAdd = (newMembers).filter(m => !existing.has(m.id));
                            return [...prev, ...toAdd];
                        });
                    }}
                />
            )}

            <div className={`gip-backdrop ${entering ? "gip-in" : ""}`} onClick={onClose} />

            <div className={`gip-panel ${entering ? "gip-in" : ""}`} role="dialog" aria-label={t("chat.group.panelTitle")}>
                <div className="gip-header">
                    <div className="gip-header-left">
                        {subTitle && (
                            <button type="button" className="gip-back-btn" onClick={closeSubPanel} aria-label={t("generics.back")}>
                                <ArrowLeft size={20} />
                            </button>
                        )}
                        <h5 className="gip-header-title">{subTitle ?? t("chat.group.panelTitle")}</h5>
                    </div>
                    <div className="gip-header-actions">
                        {showAllMembers && isAdmin && (
                            <button className="gip-add-btn" onClick={() => setShowAddMember(true)} title={t("chat.group.addParticipant")}>
                                <UserPlus size={15} />
                            </button>
                        )}
                        {!subTitle && (
                            <button type="button" className="gip-close-btn" onClick={onClose} aria-label={t("chat.group.cancel")}>
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="gip-body">

                    {/* ── Sub-panels (hides everything else) ── */}
                    {showAllMembers ? (
                        <div className="gip-all-members-panel">
                            <div className="gip-search-row">
                                <Search size={14} className="gip-search-icon" />
                                <input
                                    className="gip-search-input"
                                    type="text"
                                    placeholder={t("generics.search")}
                                    value={memberSearch}
                                    autoFocus
                                    onChange={e => { setMemberSearch(e.target.value); debouncedSetSearch(e.target.value); }}
                                />
                            </div>

                            <div className="gip-member-list">
                                {loadingAllMembers ? (
                                    [1, 2, 3].map(i => (
                                        <div key={i} className="gip-member-row">
                                            <span className="gip-skeleton gip-skeleton-avatar" />
                                            <div className="gip-member-info">
                                                <span className="gip-skeleton gip-skeleton-line" style={{ width: "55%" }} />
                                                <span className="gip-skeleton gip-skeleton-line" style={{ width: "35%" }} />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    allMembers.map(m => {
                                        const isMe = m.id === user?.id;
                                        const canToggleAdmin = isCreator && !isCommunityChat && !isMe && !m.is_creator;
                                        const canRemove = isAdmin && !isMe && !m.is_creator && (isCreator || !m.is_admin);

                                        return (
                                            <div key={m.id} className="gip-member-row">
                                                <img
                                                    className="gip-member-img"
                                                    src={getImageUser(m.img_account)}
                                                    onError={(e) => { e.currentTarget.src = getImageUserNotFound(m.name); }}
                                                />
                                                <div className="gip-member-info">
                                                    <span className="gip-member-name">{m.name}</span>
                                                    <span className="gip-member-sub">
                                                        @{m.user_name}
                                                        {m.is_creator && <span className="gip-admin-badge gip-creator-badge">{t("chat.group.creator")}</span>}
                                                        {!m.is_creator && m.is_admin && <span className="gip-admin-badge">{t("chat.group.admin")}</span>}
                                                    </span>
                                                </div>
                                                <div className="gip-member-actions">
                                                    {canToggleAdmin && (
                                                        <button
                                                            className={`gip-icon-btn ${m.is_admin ? "gip-demote-btn" : "gip-promote-btn"}`}
                                                            title={m.is_admin ? t("chat.group.demoteAdmin") : t("chat.group.promoteAdmin")}
                                                            disabled={togglingAdminId === m.id}
                                                            onClick={() => toggleAdmin(m.id, m.is_admin)}
                                                        >
                                                            {togglingAdminId === m.id
                                                                ? <span className="gip-spinner" />
                                                                : m.is_admin ? <ShieldOff size={14} /> : <Shield size={14} />
                                                            }
                                                        </button>
                                                    )}
                                                    {canRemove && (
                                                        <button
                                                            className="gip-icon-btn gip-remove-btn"
                                                            title={t("chat.group.removeMember")}
                                                            onClick={() => removeMember(m.id)}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    ) : showMedia ? (
                        <GipMediaPanel
                            t={t}
                            mediaTab={mediaTab}
                            mediaItems={mediaItems}
                            mediaCursor={mediaCursor}
                            mediaHasMore={mediaHasMore}
                            loadingMedia={loadingMedia}
                            onSwitchTab={switchMediaTab}
                            onLoadMore={() => loadMedia(mediaTab, mediaCursor)}
                            onScrollToMessage={(id) => { setShowMedia(false); onClose(); onScrollToMessage?.(id); }}
                            parsePaths={parsePaths}
                            parseTypes={parseTypes}
                            extractUrl={extractUrl}
                            formatDate={formatDate}
                            BUCKET_PACOCA_URL={BUCKET_PACOCA_URL}
                        />
                    ) : showPermissions ? (
                        <div className="gip-all-members-panel">
                            <div className="gip-section">
                                <div className="gip-permission-row">
                                    <div className="gip-permission-info">
                                        <span className="gip-permission-label">{t("chat.group.onlyAdminsPost")}</span>
                                        <span className="gip-permission-desc">
                                            {onlyAdminPost ? t("chat.group.onlyAdminsPostActive") : t("chat.group.onlyAdminsPostInactive")}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`gip-toggle ${onlyAdminPost ? "gip-toggle-on" : ""}`}
                                        onClick={toggleOnlyAdminPost}
                                        disabled={togglingOnlyAdmin}
                                        aria-label={t("chat.group.onlyAdminsPost")}
                                    >
                                        <span className="gip-toggle-thumb" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : showMsgSearch ? (
                        <GipSearchPanel
                            t={t}
                            query={msgSearchQuery}
                            results={msgResults}
                            loading={loadingMsgSearch}
                            onQueryChange={(v) => { setMsgSearchQuery(v); debouncedSetMsgSearch(v); }}
                            onClickResult={(id) => { setShowMsgSearch(false); onClose(); onScrollToMessage?.(id); }}
                            highlightText={highlightText}
                            formatDate={formatDate}
                        />
                    ) : showFavorites ? (
                        <GipFavoritesPanel
                            t={t}
                            favorites={favorites}
                            loading={loadingFavorites}
                            hasMore={favoritesHasMore}
                            onLoadMore={() => loadFavorites(favoritesCursor)}
                            onClickResult={(id) => { setShowFavorites(false); onClose(); onScrollToMessage?.(id); }}
                            formatDate={formatDate}
                        />
                    ) : (
                    <>

                    {/* Avatar */}
                    <div className="gip-avatar-wrap">
                        <div className="gip-avatar-circle">
                            {displayImg ? (
                                <img src={getImageUser(displayImg)} className="gip-avatar-img" onError={(e) => { e.currentTarget.src = "/img/img-account.png"; }} />
                            ) : (
                                <div className="gip-avatar-placeholder">
                                    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                </div>
                            )}
                            {canEditGroupInfo && (
                                <>
                                    <label className="gip-avatar-edit" htmlFor="group-avatar-input" title={t("chat.group.changePhoto")}>
                                        {savingImg ? <span className="gip-spinner" /> : <Camera size={15} />}
                                    </label>
                                    <input id="group-avatar-input" type="file" accept="image/*" style={{ display: "none" }} onChange={onAvatarFileSelected} />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Name */}
                    <div className="gip-name-row">
                        {editingName ? (
                            <>
                                <input
                                    className="gip-name-input"
                                    value={nameValue}
                                    maxLength={100}
                                    autoFocus
                                    onChange={e => setNameValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                                />
                                <button className="gip-icon-btn" onClick={saveName} disabled={savingName} title={t("chat.group.saveTitle")}>
                                    {savingName ? <span className="gip-spinner" /> : <Check size={16} />}
                                </button>
                                <button className="gip-icon-btn" onClick={() => setEditingName(false)} title={t("chat.group.cancelEdit")}>
                                    <X size={16} />
                                </button>
                            </>
                        ) : (
                            <>
                                <h5 className="gip-name">{displayName}</h5>
                                {canEditGroupInfo && (
                                    <button className="gip-icon-btn" onClick={() => { setNameValue(displayName); setEditingName(true); }} title={t("chat.group.editName")}>
                                        <Pencil size={14} />
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    <p className="gip-subtitle">{t("chat.group.panelTitle")} · {t("chat.group.member", { count: members.length })}</p>

                    {/* Description */}
                    {editingDesc ? (
                        <div className="gip-bio">
                            <textarea
                                className="gip-description-textarea"
                                value={descValue}
                                maxLength={500}
                                autoFocus
                                placeholder={t("chat.group.descriptionPlaceholder")}
                                onChange={e => setDescValue(e.target.value)}
                                onKeyDown={e => { if (e.key === "Escape") setEditingDesc(false); }}
                            />
                            <div className="gip-description-actions">
                                <button className="gip-icon-btn" onClick={saveDescription} disabled={savingDesc} title={t("chat.group.saveTitle")}>
                                    {savingDesc ? <span className="gip-spinner" /> : <Check size={16} />}
                                </button>
                                <button className="gip-icon-btn" onClick={() => setEditingDesc(false)} title={t("chat.group.cancelEdit")}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                    ) : description ? (
                        <div className="gip-bio gip-bio-row">
                            <ExpandableText
                                text={description}
                                showMoreLabel={t("chat.group.showMore")}
                                showLessLabel={t("chat.group.showLess")}
                            />
                            {canEditGroupInfo && (
                                <button className="gip-icon-btn" onClick={() => { setDescValue(description); setEditingDesc(true); }} title={t("chat.group.editDescription")}>
                                    <Pencil size={14} />
                                </button>
                            )}
                        </div>
                    ) : canEditGroupInfo ? (
                        <div className="gip-bio">
                            <button className="gip-see-more-btn" onClick={() => { setDescValue(""); setEditingDesc(true); }}>
                                + {t("chat.group.addDescription")}
                            </button>
                        </div>
                    ) : null}

                    {/* ── Entry rows: Permissões / Mídia / Pesquisar / Favoritos ── */}
                    <div className="gip-section gip-entry-section">
                        {isAdmin && (
                            <div className="gip-entry-row" onClick={() => setShowPermissions(true)}>
                                <span className="gip-entry-icon"><Shield size={17} /></span>
                                <span className="gip-entry-label">{t("chat.group.permissions")}</span>
                                <span className="gip-entry-chevron">›</span>
                            </div>
                        )}
                        <div className="gip-entry-row" onClick={openMedia}>
                            <span className="gip-entry-icon"><Image size={17} /></span>
                            <span className="gip-entry-label">{t("chat.group.mediaLinksTitle")}</span>
                            <span className="gip-entry-chevron">›</span>
                        </div>
                        <div className="gip-entry-row" onClick={openMsgSearch}>
                            <span className="gip-entry-icon"><Search size={17} /></span>
                            <span className="gip-entry-label">{t("chat.group.searchMessages")}</span>
                            <span className="gip-entry-chevron">›</span>
                        </div>
                        <div className="gip-entry-row" onClick={() => { setShowFavorites(true); loadFavorites(null); }}>
                            <span className="gip-entry-icon"><Star size={17} /></span>
                            <span className="gip-entry-label">{t("chat.favorites")}</span>
                            <span className="gip-entry-chevron">›</span>
                        </div>
                    </div>

                    {/* Members preview */}
                    <div className="gip-section">
                        <div className="gip-section-title">
                            {t("chat.group.participants")} {totalMembers > 0 ? `(${totalMembers})` : ""}
                            {isAdmin && (
                                <button className="gip-add-btn" onClick={() => setShowAddMember(true)} title={t("chat.group.addParticipant")}>
                                    <UserPlus size={15} />
                                </button>
                            )}
                        </div>

                        <div className="gip-member-list">
                            {loadingMembers ? (
                                [1, 2, 3].map(i => (
                                    <div key={i} className="gip-member-row">
                                        <span className="gip-skeleton gip-skeleton-avatar" />
                                        <div className="gip-member-info">
                                            <span className="gip-skeleton gip-skeleton-line" style={{ width: "55%" }} />
                                            <span className="gip-skeleton gip-skeleton-line" style={{ width: "35%" }} />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                members.map(m => (
                                    <div key={m.id} className="gip-member-row">
                                        <img
                                            className="gip-member-img"
                                            src={getImageUser(m.img_account)}
                                            onError={(e) => { e.currentTarget.src = getImageUserNotFound(m.name); }}
                                        />
                                        <div className="gip-member-info">
                                            <span className="gip-member-name">{m.name}</span>
                                            <span className="gip-member-sub">
                                                @{m.user_name}
                                                {m.is_creator && <span className="gip-admin-badge gip-creator-badge">{t("chat.group.creator")}</span>}
                                                {!m.is_creator && m.is_admin && <span className="gip-admin-badge">{t("chat.group.admin")}</span>}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {!loadingMembers && totalMembers > PREVIEW_LIMIT && (
                            <span className="gip-see-all-link" onClick={openAllMembers}>
                                {t("community.seeAllMembers")}
                            </span>
                        )}
                    </div>

                    {/* Created at */}
                    {chatSelected.created_at && (() => {
                        const d = new Date(chatSelected.created_at);
                        const date = d.toLocaleDateString("pt-BR");
                        const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                        return (
                            <p className="gip-created-at">{t("chat.group.createdAt", { date, time })}</p>
                        );
                    })()}

                    {/* Community link or Leave / Delete */}
                    {chatSelected.is_community_chat ? (
                        <div className="gip-leave-wrap">
                            <a
                                className="gip-leave-btn gip-community-btn"
                                href={`/comunidades/${chatSelected.community_id}`}
                            >
                                <span className="gip-leave-icon"><ExternalLink size={15} /></span>
                                Ver comunidade
                            </a>
                        </div>
                    ) : (
                        <div className="gip-leave-wrap">
                            {!isCreator && (
                                <button className="gip-leave-btn" onClick={leaveGroup}>
                                    <span className="gip-leave-icon"><LogOut size={15} /></span>
                                    {t("chat.group.leaveGroup")}
                                </button>
                            )}

                            {isCreator && !confirmDelete && (
                                <button className="gip-leave-btn" onClick={() => setConfirmDelete(true)}>
                                    <span className="gip-leave-icon"><Trash2 size={15} /></span>
                                    {t("chat.group.deleteGroup")}
                                </button>
                            )}

                            {isCreator && confirmDelete && (
                                <div className="gip-confirm-delete">
                                    <span className="gip-confirm-icon"><AlertTriangle size={15} /></span>
                                    <span className="gip-confirm-text">{t("chat.group.deleteGroupConfirm")}</span>
                                    <div className="gip-confirm-actions">
                                        <button className="gip-confirm-btn gip-confirm-cancel" onClick={() => setConfirmDelete(false)}>
                                            {t("chat.group.cancel")}
                                        </button>
                                        <button className="gip-confirm-btn gip-confirm-danger" onClick={deleteGroup} disabled={deletingGroup}>
                                            {deletingGroup ? <span className="gip-spinner gip-spinner-dark" /> : t("chat.group.delete")}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    </>
                    )}

                </div>
            </div>
        </>
    );
};

// ── GipMediaPanel ────────────────────────────────────────────────────────────
interface GipMediaPanelProps {
    t: any;
    mediaTab: "media" | "links" | "docs";
    mediaItems: MediaMsg[];
    mediaCursor: number | null;
    mediaHasMore: boolean;
    loadingMedia: boolean;
    onSwitchTab: (tab: "media" | "links" | "docs") => void;
    onLoadMore: () => void;
    onScrollToMessage: (id: number) => void;
    parsePaths: (raw?: string) => string[];
    parseTypes: (raw?: string) => string[];
    extractUrl: (text?: string) => string | null;
    formatDate: (iso: string) => string;
    BUCKET_PACOCA_URL: string;
}

export function GipMediaPanel({ t, mediaTab, mediaItems, mediaCursor, mediaHasMore, loadingMedia, onSwitchTab, onLoadMore, onScrollToMessage, parsePaths, parseTypes, extractUrl, formatDate, BUCKET_PACOCA_URL }: GipMediaPanelProps) {
    const emptyKey = mediaTab === "media" ? "noMedia" : mediaTab === "links" ? "noLinks" : "noDocs";

    return (
        <div className="gip-all-members-panel">
            {/* Tabs */}
            <div className="gip-media-tabs">
                {(["media", "links", "docs"] as const).map(tab => (
                    <button
                        key={tab}
                        className={`gip-media-tab${mediaTab === tab ? " gip-media-tab-active" : ""}`}
                        onClick={() => onSwitchTab(tab)}
                    >
                        {t(`chat.group.${tab === "media" ? "mediaTab" : tab === "links" ? "linksTab" : "docsTab"}`)}
                    </button>
                ))}
            </div>

            <div
                className="gip-media-body"
                onScroll={(e) => {
                    const el = e.currentTarget;
                    if (mediaHasMore && !loadingMedia && el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
                        onLoadMore();
                    }
                }}
            >
                {loadingMedia && mediaItems.length === 0 && (
                    <div className="gip-media-loading">
                        <span className="gip-spinner" style={{ width: 24, height: 24 }} />
                    </div>
                )}

                {!loadingMedia && mediaItems.length === 0 && (
                    <div className="gip-media-empty">
                        {mediaTab === "media" && <Image size={36} opacity={0.35} />}
                        {mediaTab === "links" && <Link size={36} opacity={0.35} />}
                        {mediaTab === "docs" && <File size={36} opacity={0.35} />}
                        <span>{t(`chat.group.${emptyKey}`)}</span>
                    </div>
                )}

                {/* ── Media grid ── */}
                {mediaTab === "media" && mediaItems.length > 0 && (
                    <div className="gip-media-grid">
                        {mediaItems.map(m => {
                            const paths = parsePaths(m.file_path);
                            const types = parseTypes(m.file_type);
                            if (m.gif_url) {
                                return (
                                    <div key={m.id} className="gip-media-cell" onClick={() => onScrollToMessage(m.id)} title={formatDate(m.created_at)}>
                                        <img src={m.gif_url} alt="GIF" loading="lazy" />
                                        <span className="gip-media-type-badge"><Video size={10} /> GIF</span>
                                    </div>
                                );
                            }
                            return paths.map((p, i) => {
                                const isVideo = types[i]?.includes("video");
                                return (
                                    <div key={`${m.id}-${i}`} className="gip-media-cell" onClick={() => onScrollToMessage(m.id)} title={formatDate(m.created_at)}>
                                        {isVideo
                                            ? <video src={`${BUCKET_PACOCA_URL}/${p}`} preload="metadata" />
                                            : <img src={`${BUCKET_PACOCA_URL}/${p}`} alt="" loading="lazy" />
                                        }
                                        {isVideo && <span className="gip-media-type-badge"><Video size={10} /></span>}
                                    </div>
                                );
                            });
                        })}
                    </div>
                )}

                {/* ── Links list ── */}
                {mediaTab === "links" && mediaItems.length > 0 && (
                    <div className="gip-links-list">
                        {mediaItems.map(m => {
                            const url = extractUrl(m.text);
                            if (!url) return null;
                            const domain = (() => { try { return new URL(url).hostname; } catch { return url; } })();
                            return (
                                <div key={m.id} className="gip-link-row" onClick={() => onScrollToMessage(m.id)}>
                                    <div className="gip-link-domain-icon">
                                        <img src={`https://www.google.com/s2/favicons?sz=32&domain_url=${domain}`} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                                    </div>
                                    <div className="gip-link-info">
                                        <span className="gip-link-url" title={url}>{domain}</span>
                                        <span className="gip-link-text">{m.text?.replace(url, "").trim().slice(0, 60)}</span>
                                        <span className="gip-link-date">{formatDate(m.created_at)}</span>
                                    </div>
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="gip-link-open" onClick={e => e.stopPropagation()}>
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── Docs list ── */}
                {mediaTab === "docs" && mediaItems.length > 0 && (
                    <div className="gip-links-list">
                        {mediaItems.map(m => {
                            const paths = parsePaths(m.file_path);
                            const names = parsePaths(m.file_name);
                            return paths.map((p, i) => (
                                <div key={`${m.id}-${i}`} className="gip-link-row" onClick={() => onScrollToMessage(m.id)}>
                                    <div className="gip-link-domain-icon gip-doc-icon">
                                        <File size={20} />
                                    </div>
                                    <div className="gip-link-info">
                                        <span className="gip-link-url">{names[i] || "arquivo"}</span>
                                        <span className="gip-link-date">{formatDate(m.created_at)}</span>
                                    </div>
                                    <a href={`${BUCKET_PACOCA_URL}/${p}`} target="_blank" rel="noopener noreferrer" className="gip-link-open" onClick={e => e.stopPropagation()}>
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            ));
                        })}
                    </div>
                )}

                {mediaHasMore && !loadingMedia && (
                    <button className="gip-load-more-btn" onClick={onLoadMore}>Carregar mais</button>
                )}
                {loadingMedia && mediaItems.length > 0 && (
                    <div style={{ textAlign: "center", padding: "10px 0" }}>
                        <span className="gip-spinner" style={{ width: 20, height: 20 }} />
                    </div>
                )}
            </div>
        </div>
    );
}

// ── GipSearchPanel ───────────────────────────────────────────────────────────
interface GipSearchPanelProps {
    t: any;
    query: string;
    results: MediaMsg[];
    loading: boolean;
    onQueryChange: (v: string) => void;
    onClickResult: (id: number) => void;
    highlightText: (text: string, q: string) => React.ReactNode;
    formatDate: (iso: string) => string;
}

export function GipSearchPanel({ t, query, results, loading, onQueryChange, onClickResult, highlightText, formatDate }: GipSearchPanelProps) {
    return (
        <div className="gip-all-members-panel">
            <div className="gip-search-row" style={{ marginBottom: 12 }}>
                <Search size={14} className="gip-search-icon" />
                <input
                    className="gip-search-input"
                    type="text"
                    placeholder={t("chat.group.searchMessagesPlaceholder")}
                    value={query}
                    autoFocus
                    onChange={e => onQueryChange(e.target.value)}
                />
            </div>

            {loading && (
                <div className="gip-media-loading">
                    <span className="gip-spinner" style={{ width: 22, height: 22 }} />
                </div>
            )}

            {!loading && query.trim() && results.length === 0 && (
                <div className="gip-media-empty">
                    <Search size={32} opacity={0.3} />
                    <span>{t("chat.group.noResults")}</span>
                </div>
            )}

            {results.map(m => (
                <div key={m.id} className="gip-search-result-row" onClick={() => onClickResult(m.id)}>
                    {m.user?.img_account ? (
                        <img className="gip-member-img" src={m.user.img_account} alt="" style={{ width: 34, height: 34, flexShrink: 0 }} />
                    ) : (
                        <div className="gip-member-img" style={{ width: 34, height: 34, flexShrink: 0, background: "var(--gip-cream-tint)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Search size={14} opacity={0.4} />
                        </div>
                    )}
                    <div className="gip-link-info" style={{ flex: 1, minWidth: 0 }}>
                        <span className="gip-member-name" style={{ fontSize: 13 }}>{m.user?.name}</span>
                        <span className="gip-link-text" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {highlightText(m.text ?? "", query)}
                        </span>
                        <span className="gip-link-date">{formatDate(m.created_at)}</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

interface GipFavoritesPanelProps {
    t: (key: string, opts?: any) => string;
    favorites: import("../../types").FavoriteMessage[];
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    onClickResult: (id: number) => void;
    formatDate: (d: string) => string;
}

export function GipFavoritesPanel({ t, favorites, loading, hasMore, onLoadMore, onClickResult, formatDate }: GipFavoritesPanelProps) {
    return (
        <div className="gip-all-members-panel">
            {loading && favorites.length === 0 && (
                <div className="gip-media-loading">
                    <span className="gip-spinner" style={{ width: 22, height: 22 }} />
                </div>
            )}

            {!loading && favorites.length === 0 && (
                <div className="gip-media-empty">
                    <Star size={32} opacity={0.3} />
                    <span>{t("chat.noFavorites")}</span>
                </div>
            )}

            {favorites.map(fav => fav.message && (
                <div key={fav.id} className="gip-search-result-row" onClick={() => onClickResult(fav.message!.id)}>
                    {fav.message.user?.img_account ? (
                        <img className="gip-member-img" src={fav.message.user.img_account} alt="" style={{ width: 34, height: 34, flexShrink: 0 }} />
                    ) : (
                        <div className="gip-member-img" style={{ width: 34, height: 34, flexShrink: 0, background: "var(--gip-cream-tint)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Star size={14} opacity={0.4} />
                        </div>
                    )}
                    <div className="gip-link-info" style={{ flex: 1, minWidth: 0 }}>
                        <span className="gip-member-name" style={{ fontSize: 13 }}>{fav.message.user?.name}</span>
                        <span className="gip-link-text" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {fav.message.text?.trim()
                                ? fav.message.text.slice(0, 120)
                                : fav.message.gif_url
                                    ? "GIF"
                                    : fav.message.file_path && JSON.parse(fav.message.file_path).length > 0
                                        ? "📎 " + t("chat.message.file")
                                        : "…"}
                        </span>
                        <span className="gip-link-date">{formatDate(fav.message.created_at)}</span>
                    </div>
                </div>
            ))}

            {hasMore && !loading && (
                <button className="gip-see-more-btn" onClick={onLoadMore} style={{ margin: "8px auto", display: "block" }}>
                    {t("chat.group.showMore")}
                </button>
            )}
            {loading && favorites.length > 0 && (
                <div className="gip-media-loading">
                    <span className="gip-spinner" style={{ width: 18, height: 18 }} />
                </div>
            )}
        </div>
    );
}

export default GroupInfoPanel;
